import { applications } from '@/features/application/application.config'
import {
  type AuthMeResponse,
  type UserPreference
} from '@/stores/api/application-api'

/**
 * Priority order used when the user has no pinned `default_application` or
 * the pinned one isn't granted in the current org. Walked top-to-bottom;
 * first app with a `:read` grant wins.
 *
 * Ordering rationale: `internal` is the broad staff dashboard; `portal` is
 * the single-app destination for external property contacts; specialist
 * apps follow; `admin` is last because anyone with admin almost always has
 * a real work app and admin isn't a primary work surface.
 */
const APP_PRIORITY = [
  'internal',
  'portal',
  'dispatching',
  'technician',
  'deployments',
  'sales',
  'logistics',
  'design',
  'metrics',
  'knowledge',
  'client-success',
  'admin'
] as const

/**
 * Apps that live outside the `[orgSlug]` segment (under their own route group)
 * and therefore have no orgSlug prefix in their URL: `admin` (the `(super)`
 * group) and `client-success` (the `(staff)` group). Both are cross-org
 * surfaces with a single active-org context rather than an org-scoped path.
 */
const NO_ORG_PREFIX_APPS = new Set(['admin', 'client-success'])

/**
 * Apps that can never be a landing destination. "My Atlas" is a personal area
 * (Inbox + Profile), not a work surface — landing there after sign-in would
 * bury the app the user actually came to use. "Vue" is the standalone AI
 * workspace, which opens in its own tab rather than being a workspace landing.
 *
 * `/api/preferences` is the real enforcement point (`me` is absent from its
 * `VALID_APP_KEYS`, so the pref can't be written in the first place) and `me`
 * is absent from `APP_PRIORITY` below so it's never an automatic fallback.
 * This set is the explicit belt-and-braces: without it the guard would rest on
 * the accident that an ungated app has no `me:read` grant for `hasReadAccess`
 * to find.
 */
const NEVER_DEFAULT_APPS = new Set(['me', 'vue'])

/** Whether an app may be pinned as the user's default. The app switcher hides
 *  its "set as default" star for apps that can't, so the affordance never
 *  offers something `/api/preferences` would reject with a 400. */
export function canBeDefaultApp(appKey: string): boolean {
  return !NEVER_DEFAULT_APPS.has(appKey)
}

/**
 * Resolves the post-sign-in / post-org-switch landing URL.
 *
 * Resolution order:
 *   1. The user's pinned `default_application` if granted in this org.
 *   2. The first app in `APP_PRIORITY` the user has `:read` on.
 *   3. `null` when the user has zero accessible apps (caller routes to
 *      `/not-authorized` or similar).
 *
 * `authMe` should reflect permissions for the *target* org. Callers must
 * refetch /auth/me after `setActiveOrganization` before calling this.
 */
export function resolveDefaultAppUrl(
  orgSlug: string,
  preferences: UserPreference[] | undefined,
  authMe: AuthMeResponse | undefined
): string | null {
  // Note: system:admin is NOT a wildcard app-read. assertPermission in app
  // layouts doesn't honor it, so short-circuiting here would land sysadmins
  // on routes the server then 403s. Sysadmins land based on actual grants.
  const hasReadAccess = (key: string) =>
    !!authMe?.permissionGrants.some((g) => g.permissionId === `${key}:read`)

  // Admin (`(super)`) and Client Success (`(staff)`) live outside the
  // `[orgSlug]` segment, so their URLs carry no orgSlug prefix. Every other
  // app is org-scoped.
  const urlForApp = (key: string) => {
    const app = applications[key as keyof typeof applications]
    return NO_ORG_PREFIX_APPS.has(key)
      ? app.baseUrl
      : `/${orgSlug}${app.baseUrl}`
  }

  const pinnedKey = preferences?.find(
    (p) => p.preference_key === 'default_application'
  )?.value

  if (
    pinnedKey &&
    pinnedKey in applications &&
    !NEVER_DEFAULT_APPS.has(pinnedKey) &&
    hasReadAccess(pinnedKey)
  ) {
    return urlForApp(pinnedKey)
  }

  for (const key of APP_PRIORITY) {
    if (key in applications && hasReadAccess(key)) {
      return urlForApp(key)
    }
  }

  return null
}
